// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, signup as apiSignup, logout as apiLogout, getMe } from '../api';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // On mount, restore user from localStorage (fast)
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('user');
      }
    }
  }, []);

  // On page load, verify the session with the server (skip public pages)
  useEffect(() => {
    const fetchUser = async () => {
      // Skip if on login or signup pages
      if (location.pathname === '/login' || location.pathname === '/signup') {
        setLoading(false);
        return;
      }

      try {
        const response = await getMe();
        if (response.status === 1 && response.data) {
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } else {
          setUser(null);
          localStorage.removeItem('user');
        }
      } catch (error) {
        // If the token is invalid, clear local state
        setUser(null);
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [location.pathname]);

  // ---------- LOGIN (UPDATED) ----------
const doLogin = async (email, password) => {
  const response = await apiLogin(email, password);
  if (response.status === 1) {
    // ✅ Only save token if it actually exists!
    if (response.data.token) {
      localStorage.setItem('token', response.data.token); 
    }
    
    const userData = response.data.user; 
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    return response;
  } else {
    throw new Error(response.description || 'Login failed');
  }
};

  // ---------- SIGNUP ----------
  const doSignup = async (name, email, password) => {
    const response = await apiSignup(name, email, password);
    if (response.status === 1 && response.data) {
      const userData = response.data.user;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return response;
    } else {
      throw new Error(response.description || 'Signup failed');
    }
  };

  // ---------- LOGOUT ----------
  const doLogout = async () => {
    try {
      await apiLogout();
    } catch (e) {
      // ignore
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('token'); // Also remove the token!
      setUser(null);
      navigate('/login');
    }
  };

  // ---------- REFRESH USER ----------
  const refreshUser = async () => {
    try {
      const response = await getMe();
      if (response.status === 1 && response.data) {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      }
    } catch (e) {
      setUser(null);
      localStorage.removeItem('user');
    }
  };

  const value = {
    user,
    loading,
    doLogin,
    doSignup,
    doLogout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}